import { useMemo, useState, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Tag,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  Plus,
  Ticket,
  ShieldAlert,
  Ban,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useVoucherListQuery,
  useVoucherStatusCountQuery,
  useVoucherTypesQuery,
} from '@/client/api/voucher/voucher.query';
import {
  deriveVoucherStatus,
  formatVoucherValue,
  scopeBadges,
  Voucher,
  VoucherListParams,
  VoucherListStatus,
} from '@/client/types/voucher';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import VoucherListFilters, {
  VoucherFilters,
} from '@/client/components/admin/Voucher/VoucherListFilters';
import VoucherDetailsDrawer from '@/client/components/admin/Voucher/VoucherDetailsDrawer';
import CreateVoucherModal from '@/client/components/admin/Voucher/CreateVoucherModal';

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
  redeemed: 'Redeemed',
};

const isListStatus = (value: string | null): value is VoucherListStatus =>
  value === 'active' || value === 'expired' || value === 'revoked' || value === 'redeemed';

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-600',
  expired: 'bg-orange-100 text-orange-700',
  exhausted: 'bg-purple-100 text-purple-800',
  revoked: 'bg-red-100 text-red-800',
};

const STATUS_ICON: Record<string, ReactNode> = {
  active: <CheckCircle2 className="h-3.5 w-3.5" />,
  inactive: <Clock className="h-3.5 w-3.5" />,
  expired: <XCircle className="h-3.5 w-3.5" />,
  exhausted: <TrendingUp className="h-3.5 w-3.5" />,
  revoked: <Ban className="h-3.5 w-3.5" />,
};

const StatCard = ({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: ReactNode;
  color: string;
}) => (
  <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-4">
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="mt-0.5 text-2xl font-black text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const UsageBar = ({ used, max }: { used: number; max: number | null | undefined }) => {
  if (max == null || max <= 0) {
    return (
      <p className="text-xs text-gray-500">
        {used.toLocaleString()} redeemed
        {max == null ? ' · single-use default' : ''}
      </p>
    );
  }
  const pct = Math.min((used / max) * 100, 100);
  const color = pct >= 90 ? 'bg-red-500' : pct >= 60 ? 'bg-yellow-400' : 'bg-green-500';
  return (
    <div className="w-full">
      <div className="mb-1 flex justify-between text-xs text-gray-500">
        <span>{used.toLocaleString()} used</span>
        <span>{max.toLocaleString()} max</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-gray-100">
        <div className={`${color} h-1.5 rounded-full`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SkeletonRow = () => (
  <tr>
    <td className="px-6 py-4">
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-3 w-16" />
      </div>
    </td>
    <td className="px-6 py-4">
      <Skeleton className="h-5 w-24 rounded-full" />
    </td>
    <td className="px-6 py-4">
      <Skeleton className="h-5 w-20 rounded-full" />
    </td>
    <td className="px-6 py-4">
      <Skeleton className="h-4 w-16" />
    </td>
    <td className="px-6 py-4">
      <Skeleton className="h-3 w-32" />
    </td>
    <td className="px-6 py-4">
      <Skeleton className="h-3 w-24" />
    </td>
    <td className="px-6 py-4 text-right">
      <Skeleton className="ml-auto h-4 w-12" />
    </td>
  </tr>
);

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
};

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing vouchers requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">voucher.view</code> permission.
    </p>
  </div>
);

const VoucherListPage = () => {
  // `status` (and every other filter) is persisted in the URL by useListFilters
  // below; this read is only for the heading/breadcrumb label.
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status');
  const urlStatus = isListStatus(statusFromUrl) ? statusFromUrl : '';

  useBreadcrumbs([
    { label: 'Vouchers', href: '/admin/vouchers' },
    { label: urlStatus ? STATUS_LABELS[urlStatus] : 'All' },
  ]);

  const viewPerm = useHasPermission('voucher.view');
  const createPerm = useHasPermission('voucher.create');
  const editPerm = useHasPermission('voucher.edit');
  const revokePerm = useHasPermission('voucher.revoke');
  const deletePerm = useHasPermission('voucher.delete');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canCreate = createPerm.isSuperAdmin || createPerm.hasPermission;
  const canEdit = editPerm.isSuperAdmin || editPerm.hasPermission;
  const canRevoke = revokePerm.isSuperAdmin || revokePerm.hasPermission;
  const canDelete = deletePerm.isSuperAdmin || deletePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [selectedVid, setSelectedVid] = useState<number | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const {
    searchTerm,
    debouncedSearchTerm,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    resetFilters,
  } = useListFilters<VoucherFilters>({
    initialFilters: { status: '', vtid: '' },
    initialLimit: 25,
  });

  const listParams: VoucherListParams = useMemo(() => {
    const offset = (page - 1) * limit;
    const status = (filters.status || '') as VoucherListStatus | '';
    return {
      limit,
      offset,
      ...(debouncedSearchTerm ? { code: debouncedSearchTerm.trim().toUpperCase() } : {}),
      ...(status ? { status } : {}),
      ...(filters.vtid ? { vtid: Number(filters.vtid) } : {}),
    };
  }, [debouncedSearchTerm, filters, page, limit]);

  const { data: types = [] } = useVoucherTypesQuery();
  const typeById = useMemo(() => {
    const map = new Map<number, (typeof types)[number]>();
    types.forEach((t) => map.set(t.vtid, t));
    return map;
  }, [types]);

  const { data: voucherData, isLoading, isError, error } = useVoucherListQuery(
    listParams,
    canView && !permLoading,
  );

  // '' = no status filter → overall total, independent of the table's filters.
  const { data: totalCount = 0 } = useVoucherStatusCountQuery('', canView && !permLoading);
  const { data: activeCount = 0 } = useVoucherStatusCountQuery('active', canView && !permLoading);
  const { data: expiredCount = 0 } = useVoucherStatusCountQuery('expired', canView && !permLoading);
  const { data: revokedCount = 0 } = useVoucherStatusCountQuery('revoked', canView && !permLoading);
  const { data: redeemedCount = 0 } = useVoucherStatusCountQuery('redeemed', canView && !permLoading);

  const vouchers = voucherData?.vouchers ?? [];
  const pagination = voucherData?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 ||
    (!permLoading && !canView);

  const handleViewDetails = (voucher: Voucher) => {
    setSelectedVid(voucher.vid);
    setIsDrawerOpen(true);
  };

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <Ticket className="h-4 w-4 text-white" />
              </div>
              <h1 className="text-2xl font-black text-gray-900">
                {urlStatus ? `${STATUS_LABELS[urlStatus]} vouchers` : 'Vouchers'}
              </h1>
            </div>
            <p className="text-sm text-gray-500">
              Payment-integrated vouchers redeemed at checkout.
            </p>
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={() => setIsCreateOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Create Voucher
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Total"
            value={totalCount.toLocaleString()}
            icon={<Tag className="h-6 w-6 text-blue-600" />}
            color="bg-blue-100"
          />
          <StatCard
            label="Active"
            value={activeCount.toLocaleString()}
            icon={<CheckCircle2 className="h-6 w-6 text-green-600" />}
            color="bg-green-100"
          />
          <StatCard
            label="Expired"
            value={expiredCount.toLocaleString()}
            icon={<Clock className="h-6 w-6 text-orange-500" />}
            color="bg-orange-100"
          />
          <StatCard
            label="Redeemed / Revoked"
            value={`${redeemedCount.toLocaleString()} / ${revokedCount.toLocaleString()}`}
            icon={<TrendingUp className="h-6 w-6 text-purple-600" />}
            color="bg-purple-100"
          />
        </div>

        <VoucherListFilters
          searchValue={searchTerm}
          filters={filters}
          types={types}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          onReset={resetFilters}
        />

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load vouchers'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Code
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Type
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Value
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Usage
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Validity
                  </th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 bg-white">
                {isLoading &&
                  Array.from({ length: Math.min(limit, 10) }).map((_, i) => (
                    <SkeletonRow key={i} />
                  ))}

                {!isLoading && vouchers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                          <Ticket className="h-8 w-8 text-gray-400" />
                        </div>
                        <p className="font-medium text-gray-500">No vouchers found</p>
                        <p className="text-sm text-gray-400">
                          {searchTerm || filters.status || filters.vtid
                            ? 'Try adjusting your filters'
                            : 'Create your first voucher to get started'}
                        </p>
                        {canCreate && !searchTerm && !filters.status && !filters.vtid && (
                          <button
                            type="button"
                            onClick={() => setIsCreateOpen(true)}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-600 transition-colors hover:bg-blue-50"
                          >
                            <Plus className="h-4 w-4" />
                            Create Voucher
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  vouchers.map((voucher) => {
                    const status = deriveVoucherStatus(voucher);
                    const typeMeta = typeById.get(voucher.vtid);
                    const badges = scopeBadges(voucher);
                    return (
                      <tr
                        key={voucher.vid}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() => handleViewDetails(voucher)}
                      >
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                              <Tag className="h-4 w-4 text-blue-500" />
                            </div>
                            <div>
                              <p className="font-mono text-sm font-bold tracking-wider text-gray-900">
                                {voucher.code}
                              </p>
                              <p className="max-w-[140px] truncate font-mono text-xs text-gray-400">
                                {voucher.ffid || `vid ${voucher.vid}`}
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {badges.slice(0, 2).map((b) => (
                                  <span
                                    key={b}
                                    className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600"
                                  >
                                    {b}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          {typeMeta?.name || `Type ${voucher.vtid}`}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                              STATUS_BADGE[status] || STATUS_BADGE.inactive
                            }`}
                          >
                            {STATUS_ICON[status]}
                            {status.toUpperCase()}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <p className="text-sm font-bold text-gray-900">
                            {formatVoucherValue(
                              voucher.value,
                              typeMeta?.value_type,
                              voucher.ccode,
                            )}
                          </p>
                          <p className="text-xs capitalize text-gray-400">
                            {(typeMeta?.value_type || 'fixed').toString().toLowerCase()}
                          </p>
                        </td>
                        <td className="min-w-[160px] px-6 py-4">
                          <UsageBar
                            used={voucher.redemption_count ?? 0}
                            max={voucher.max_redemptions}
                          />
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="space-y-0.5 text-xs text-gray-500">
                            <div>
                              <span className="font-medium text-gray-700">From </span>
                              {formatDate(voucher.date_valid_from)}
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">To </span>
                              {formatDate(voucher.date_valid_until)}
                            </div>
                          </div>
                        </td>
                        <td
                          className="whitespace-nowrap px-6 py-4 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => handleViewDetails(voucher)}
                            className="text-sm font-semibold text-blue-600 transition-colors hover:text-blue-800"
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-100 bg-white px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons={true}
              showSelect={true}
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      <VoucherDetailsDrawer
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        vid={selectedVid}
        types={types}
        canEdit={canEdit}
        canRevoke={canRevoke}
        canDelete={canDelete}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedVid(null);
        }}
      />

      {canCreate && (
        <CreateVoucherModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      )}
    </div>
  );
};

export default VoucherListPage;
